/**
 * Routine Finder — Main Controller
 * Depends on: window.rfQuestions, window.rfResults, window.rfShopify,
 *             window.rfSupabase, window.explanationService
 *
 * Boot: self-invoked on DOMContentLoaded (or immediately if DOM is ready).
 * If dependencies are not yet loaded, retries every 30ms (max 50 attempts).
 */
(function () {
  'use strict';

  // ─── State ────────────────────────────────────────────────────────────────
  let _step        = 0;
  let _answers     = {};
  let _email       = '';
  let _recs        = null;
  let _modal       = null;
  let _lastFocusEl = null;
  let _sessionId      = null;  // Supabase tracking session
  let _quizStartedAt  = null;  // Timestamp for time-spent tracking

  // ─── DOM ──────────────────────────────────────────────────────────────────
  const $ = (id) => document.getElementById(id);

  const SCREENS = ['rf2-intro', 'rf2-quiz', 'rf2-email', 'rf2-loading', 'rf2-results'];

  function showScreen(name) {
    SCREENS.forEach(id => {
      const el = $(id);
      if (el) el.hidden = (id !== `rf2-${name}`);
    });
  }

  /** XSS-safe string for innerHTML — always use when inserting untrusted data */
  function esc(str) {
    return String(str ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  // ─── Concern icon URLs (Shopify CDN) ──────────────────────────────────────
  // IMPORTANT: Verify these URLs match your uploaded filenames in Shopify Admin → Files
  const _CDN = 'https://cdn.shopify.com/s/files/1/0236/1258/8078/files/';
  const CONCERN_ICONS = {
    fine_lines:        _CDN + 'Fine_Lines_Wrinkles.jpg',
    firmness:          _CDN + 'Loss_of_Firmness_Elasticity.jpg',
    congestion:        _CDN + 'Congestion_or_Hormonal_Breakouts.jpg',
    hyperpigmentation: _CDN + 'Hyperpigmentation_or_Dark_Spots.jpg',
    acne_marks:        _CDN + 'Post-acne_Marks.jpg',
    sun_damage:        _CDN + 'Sun_Damage.jpg',
    texture:           _CDN + 'Rough_or_Uneven_Texture.jpg',
    sensitivity:       _CDN + 'Sensitivity_Redness_Reactive.jpg',
    enlarged_pores:    _CDN + 'Enlarged_Pores.jpg',
    oily:              _CDN + 'Excess_Oil.jpg',
    dehydrated:        _CDN + 'Dehydration.jpg',
    dull:              _CDN + 'Dullness_or_Lack_of_Radiance.jpg',
  };

  // ─── When-to-use — derived from master registry ────────────────────────────
  function _getWhenToUse(productId) {
    return window.rfProducts?.[productId]?.whenToUse || null;
  }
  // Product flags helper
  function _getFlags(productId) {
    return window.rfProducts?.[productId]?.flags || {};
  }

  // SVG icons for AM / PM
  const _SUN_SVG  = '<svg class="rf2-wtu__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>';
  const _MOON_SVG = '<svg class="rf2-wtu__icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>';

  function _whenToUseHtml(productId) {
    const wtu = _getWhenToUse(productId);
    if (!wtu) return '';
    const icons = (wtu.am ? `<span class="rf2-wtu__badge" aria-label="Morning">${_SUN_SVG}</span>` : '')
                + (wtu.pm ? `<span class="rf2-wtu__badge" aria-label="Night">${_MOON_SVG}</span>` : '');
    return `<div class="rf2-when-to-use">`
         + `<span class="rf2-wtu__label">When to use</span>`
         + `<span class="rf2-wtu__icons">${icons}</span>`
         + `<p class="rf2-wtu__text">${esc(wtu.text)}</p>`
         + `</div>`;
  }

  /** Build concern chip HTML for a product */
  function _concernChipHtml(product) {
    // Cleansers only earn a concern chip if they rank 1 or 2 for that concern.
    // A rank of 3+ means the cleanser is a generic inclusion, not a targeted fix.
    if (product.stepName === 'Cleanse' && (product.concernScore || 0) > 2) return '';
    const type  = product.concernType || 'primary';
    const key   = product.concernKey  || '';
    const label = (window.rfResults?.CONCERN_LABELS?.[key] || key);
    const icon  = CONCERN_ICONS[key] || '';
    if (!icon) return '';
    return `<span class="rf2-concern-chip rf2-concern-chip--${esc(type)}">` +
      `<img class="rf2-concern-chip__icon" src="${esc(icon)}" alt="" width="18" height="18" loading="lazy">` +
      `<span class="rf2-concern-chip__text">${esc(label)}</span>` +
    `</span>`;
  }

  // ─── Init ─────────────────────────────────────────────────────────────────
  let _retries = 0;
  function init() {
    if (!window.rfQuestions || !window.rfResults) {
      if (++_retries < 50) { setTimeout(init, 30); }
      return;
    }

    // ── PDP Mini Quiz mode: separate lightweight path ──
    if (window.rfMiniQuiz?.active) { _initMini(); return; }

    $('rf2-begin')?.addEventListener('click', startQuiz);
    $('rf2-back')?.addEventListener('click', prevStep);
    $('rf2-email-form')?.addEventListener('submit', handleEmailSubmit);
    $('rf2-skip-email')?.addEventListener('click', () => {
      window.rfSupabase?.updateSession(_sessionId, { email_skipped: true });
      processResults();
    });
    $('rf2-restart')?.addEventListener('click', startOver);
    $('rf2-add-routine')?.addEventListener('click', handleAddFullRoutine);
    // Welcome-back banner retake button — clears saved session then restarts quiz
    $('rf2-welcome-retake')?.addEventListener('click', () => {
      _clearSavedSession();
      startOver();
    });

    _modal = _createModal();

    const token = new URLSearchParams(window.location.search).get('routine');
    if (token) {
      // Explicit share URL — always takes priority over any saved session
      loadRoutineByToken(token);
    } else {
      const saved = _loadSavedSession();
      if (saved) {
        _restoreSavedSession(saved);
      } else {
        showScreen('intro');
      }
    }
  }

  // ─── Quiz ─────────────────────────────────────────────────────────────────
  let _cameFromScreen = 'intro'; // tracks which screen user came from (for back on Q1)

  async function startQuiz() {
    // Remember where user came from so back on Q1 can return there
    _cameFromScreen = _recs ? 'results' : 'intro';
    _step    = 0;
    _answers = {};
    _quizStartedAt = Date.now();
    renderQuestion();
    showScreen('quiz');

    // Start a tracking session (non-blocking)
    if (window.rfSupabase?.startSession) {
      const res = await window.rfSupabase.startSession();
      if (res?.session_id) {
        _sessionId = res.session_id;
        // Tag session source
        window.rfSupabase.updateSession(_sessionId, { source: 'routine_finder' });
        // Carry over saved email from previous sessions
        const savedEmail = _getSavedEmail();
        if (savedEmail) {
          _email = savedEmail;
          window.rfSupabase.updateSession(_sessionId, { email: savedEmail });
        }
      }
    }
  }

  function renderQuestion() {
    const questions = window.rfQuestions;
    const q         = questions[_step];
    const total     = questions.length;
    const pct       = Math.round((_step / total) * 100);

    const bar = $('rf2-progress-fill');
    bar.style.width = `${pct}%`;
    bar.parentElement.setAttribute('aria-valuenow', pct);

    $('rf2-q-counter').textContent  = `${_step + 1} / ${total}`;
    $('rf2-q-title').textContent    = q.title;

    const subtitleEl = $('rf2-q-subtitle');
    subtitleEl.textContent = q.subtitle || '';
    subtitleEl.hidden      = !q.subtitle;

    // Always show back button — on Q1 it returns to intro/results
    $('rf2-back').hidden = false;

    // Build options — filter out previously selected value if filterOut is set
    let opts = q.options;
    if (q.filterOut && _answers[q.filterOut]) {
      opts = opts.filter(o => o.value !== _answers[q.filterOut]);
    }

    const container = $('rf2-options');
    container.className = `rf2-options${opts.length > 6 ? ' rf2-options--grid' : ''}`;
    container.setAttribute('role', 'listbox');
    container.setAttribute('aria-label', q.title);
    container.innerHTML = '';

    opts.forEach((opt, idx) => {
      const btn = document.createElement('button');
      btn.type             = 'button';
      btn.className        = `rf2-option${_answers[q.id] === opt.value ? ' rf2-option--selected' : ''}`;
      btn.textContent      = opt.label;
      btn.dataset.value    = opt.value;
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', _answers[q.id] === opt.value ? 'true' : 'false');
      btn.addEventListener('click', () => selectOption(q.id, opt.value, btn));
      // Arrow key navigation between options
      btn.addEventListener('keydown', (e) => {
        const siblings = Array.from(container.querySelectorAll('.rf2-option'));
        let next;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          next = siblings[idx + 1] || siblings[0];
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          next = siblings[idx - 1] || siblings[siblings.length - 1];
        }
        if (next) next.focus();
      });
      container.appendChild(btn);
    });
  }

  let _optionLocked = false; // debounce guard for rapid option clicks

  function selectOption(questionId, value, clickedBtn) {
    if (_optionLocked) return;
    _optionLocked = true;
    _answers[questionId] = value;
    $('rf2-options').querySelectorAll('.rf2-option').forEach(btn => {
      btn.classList.toggle('rf2-option--selected', btn === clickedBtn);
    });

    // Track step progress — store 1-indexed count of completed questions (1–7)
    window.rfSupabase?.updateSession(_sessionId, { last_step_reached: _step + 1 });

    setTimeout(() => { _optionLocked = false; advance(); }, 280);
  }

  function advance() {
    if (_step < window.rfQuestions.length - 1) {
      _step++;
      renderQuestion();
    } else {
      const saved = _getSavedEmail();
      if (saved) {
        _email = saved;
        processResults();
      } else {
        showScreen('email');
      }
    }
  }

  function prevStep() {
    if (_step > 0) {
      _step--;
      renderQuestion();
    } else {
      // Q1 — go back to wherever user came from
      if (_cameFromScreen === 'results' && _recs) {
        showScreen('results');
      } else {
        showScreen('intro');
      }
    }
  }

  // ─── Email ────────────────────────────────────────────────────────────────
  function handleEmailSubmit(e) {
    e.preventDefault();
    const input   = $('rf2-email-input');
    const errorEl = $('rf2-email-error');
    const val     = input.value.trim();
    const valid   = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val);

    errorEl.hidden = valid;
    input.toggleAttribute('aria-invalid', !valid);
    if (!valid) { input.focus(); return; }

    _email = val;
    _setSavedEmail(val);
    window.rfSupabase?.updateSession(_sessionId, { email: val, email_skipped: false });
    processResults();
  }

  // ─── Results pipeline ─────────────────────────────────────────────────────
  async function processResults() {
    showScreen('loading');

    try {
      // 1 — Score and rank products
      _recs = window.rfResults.generateRoutineRecommendations(_answers);
      const all = [_recs.hero, ..._recs.supporting];

      // 2 — Enrich with live Shopify data (parallel, non-blocking on failure)
      if (window.rfShopify?.enrichProducts) {
        await window.rfShopify.enrichProducts(all);
      }

      // 3 — Personalised explanations
      if (window.explanationService?.generatePersonalizedExplanation) {
        all.forEach(p => {
          p.explanation = window.explanationService.generatePersonalizedExplanation(p.id, _answers);
        });
      }

      // 4 — Analytics (non-blocking)
      _trackKlaviyo(_recs);
      window.rfSupabase?.saveQuizCompletion({
        answers:         _answers,
        email:           _email,
        hero:            _recs.hero.id,
        recommendations: all.map(p => p.id),
      });

      // 4b — Session completion tracking (Supabase PostgreSQL)
      const timeSpent = _quizStartedAt ? Math.round((Date.now() - _quizStartedAt) / 1000) : null;
      const completionP = window.rfSupabase?.completeSession?.(_sessionId, {
        completed:            true,
        answers:              _answers,
        email:                _email || null,
        hero_product:         _recs.hero.id,
        recommended_products: all.map(p => p.id),
        routine_headline:     _recs.routineHeadline,
        time_spent_seconds:   timeSpent,
        source:               'routine_finder',
      });
      completionP?.then(result => {
        if (result?.share_token) { _saveSession(result.share_token, _answers, _recs.hero.id, _sessionId); }
      });

      // 5 — Render
      renderResults();
      showScreen('results');
    } catch (err) {
      console.error('[RF] processResults failed:', err);
      // Show a user-friendly error and let them retry
      const loadingText = $('rf2-loading')?.querySelector('.rf2-loading__text');
      if (loadingText) {
        loadingText.textContent = 'Something went wrong — please try again.';
      }
      // Allow retake to recover
      setTimeout(() => showScreen('intro'), 3000);
    }
  }

  // ─── Render ───────────────────────────────────────────────────────────────
  function renderResults() {
    const { hero, supporting, routineHeadline } = _recs;
    const LABELS = window.rfResults.CONCERN_LABELS;

    $('rf2-primary-tag').textContent      = LABELS[_answers.primary_concern]   || '';
    $('rf2-secondary-tag').textContent    = LABELS[_answers.secondary_concern] || '';
    $('rf2-routine-headline').textContent = routineHeadline;

    const heroSection = $('rf2-hero-section');
    heroSection.innerHTML = '';
    heroSection.appendChild(buildHeroCard(hero));

    const list = $('rf2-supporting-list');
    list.innerHTML = '';
    if (supporting.length) {
      list.style.setProperty('--rf2-cols', Math.min(supporting.length, 4));
      $('rf2-supporting-divider').hidden = false;
      supporting.forEach((p, i) => list.appendChild(buildSupportingCard(p, i)));
    }

    // Full-routine CTA total with bundle discount
    const allProducts = [hero, ...supporting];
    const total       = allProducts.reduce((s, p) => s + (p.price || 0), 0);
    if (total > 0) {
      const fmt      = window.rfShopify?.formatPrice;
      const code     = hero.currencyCode;
      const count    = allProducts.filter(p => p.available !== false && p.shopifyVariantId).length;
      const discount = _getBundleDiscount(count);

      if (discount > 0) {
        const discounted = total * (1 - discount / 100);
        const fmtOrig    = fmt ? fmt(total, code)       : `£${total.toFixed(2)}`;
        const fmtDisc    = fmt ? fmt(discounted, code)   : `£${discounted.toFixed(2)}`;
        $('rf2-add-routine').textContent = `Add Full Routine \u2014 ${fmtDisc}`;
        $('rf2-routine-cta-subtext').innerHTML =
          `<s>${esc(fmtOrig)}</s> \u2014 Save ${discount}% with your complete routine`;
      } else {
        const price = fmt ? fmt(total, code) : `£${total}`;
        $('rf2-add-routine').textContent = `Add Full Routine \u2014 ${price}`;
        $('rf2-routine-cta-subtext').textContent =
          `${count} products curated for `
          + `${(LABELS[_answers.primary_concern] || '').toLowerCase()} and `
          + `${(LABELS[_answers.secondary_concern] || '').toLowerCase()}`;
      }
      // Dynamic shipping message: only claim free shipping if the total meets the threshold
      const shippingEl = $('rf2-routine-cta-shipping');
      if (shippingEl) {
        const finalTotal  = discount > 0 ? total * (1 - discount / 100) : total;
        const fsThreshold = _getFreeShippingAmount();
        shippingEl.textContent = finalTotal >= fsThreshold
          ? '\u2713 Free shipping included'
          : _getFreeShippingText();
      }

      $('rf2-routine-cta').hidden = false;
    }


  }

  function buildHeroCard(product) {
    const article  = document.createElement('article');
    article.className = 'rf2-hero-card';

    const price    = window.rfShopify?.formatPrice
      ? window.rfShopify.formatPrice(product.price, product.currencyCode)
      : `£${product.price}`;
    const outOfStock  = product.available === false;
    const noVariant   = !product.shopifyVariantId;
    const disableAttr = (outOfStock || noVariant) ? 'disabled aria-disabled="true"' : '';
    const btnLabel    = outOfStock ? 'Out of stock' : `Add to Bag \u2014 ${price}`;

    article.innerHTML = `
      <header class="rf2-hero-card__header">
        <span class="rf2-badge rf2-badge--recommend">#1 Recommendation</span>
      </header>
      <div class="rf2-hero-card__grid">
        <div class="rf2-hero-card__media">
          ${product.imageUrl
            ? `<img src="${esc(product.imageUrl)}" alt="${esc(product.name)}" loading="eager" width="600" height="750">`
            : '<div class="rf2-hero-card__img-placeholder"></div>'
          }
        </div>
        <div class="rf2-hero-card__body">
          <h2 class="rf2-hero-card__name">
            <a href="${esc(product.productUrl || '#')}" target="_blank" rel="noopener">${esc(product.name)}</a>
          </h2>
          <p class="rf2-hero-card__tagline">${esc(product.tagline || '')}</p>
          <div class="rf2-hero-card__concerns">
            ${_concernChipHtml(product)}
            ${product.secondaryConcernKey
              ? _concernChipHtml({ ...product, concernType: 'secondary', concernKey: product.secondaryConcernKey })
              : ''}
          </div>
          <div class="rf2-hero-card__why">
            <p class="rf2-why-label">Why this is recommended for you</p>
            <p class="rf2-why-text">${esc(product.explanation || '')}</p>
          </div>
          ${_getFlags(product.id).sensitivityTip && _answers.sensitised === 'yes'
            ? '<p class="rf2-sensitivity-tip">If your skin is easily sensitised, start by applying a few drops of TPC into your moisturiser.</p>'
            : ''
          }
          ${_whenToUseHtml(product.id)}
          <button class="btn btn--primary rf2-atb-btn"
                  type="button"
                  data-variant-id="${esc(product.shopifyVariantId || '')}"
                  ${disableAttr}>
            ${esc(btnLabel)}
          </button>
          <div class="rf2-micro-trust">
            <span>&#10003; ${esc(_getFreeShippingText())}</span>
            <span>&#10003; Cruelty free</span>
            ${_getFlags(product.id).clinicallyTested ? '<span>&#10003; Clinically tested</span>' : ''}
          </div>
        </div>
      </div>
    `;

    article.querySelector('.rf2-atb-btn')?.addEventListener('click', (e) => {
      const vid = e.currentTarget.dataset.variantId;
      if (vid) _handleAddToCart(vid, e.currentTarget, product.id);
    });

    return article;
  }

  function buildSupportingCard(product, index) {
    const article = document.createElement('article');
    article.className = 'rf2-supporting-card';
    article.style.setProperty('--rf2-card-index', index);

    const price       = window.rfShopify?.formatPrice
      ? window.rfShopify.formatPrice(product.price, product.currencyCode)
      : `£${product.price}`;
    const outOfStock  = product.available === false;
    const noVariant   = !product.shopifyVariantId;
    const disableAttr = (outOfStock || noVariant) ? 'disabled aria-disabled="true"' : '';
    const btnLabel    = outOfStock ? 'Out of stock' : `Add to Bag \u2014 ${price}`;

    article.innerHTML = `
      <div class="rf2-supporting-card__media">
        ${product.imageUrl
          ? `<img src="${esc(product.imageUrl)}" alt="${esc(product.name)}" loading="lazy" width="400" height="500">`
          : '<div class="rf2-supporting-card__img-placeholder"></div>'
        }
      </div>
      <div class="rf2-supporting-card__body">
        <p class="rf2-step-label">${esc(product.stepName)}</p>
        <h3 class="rf2-supporting-card__name">
          <a href="${esc(product.productUrl || '#')}" target="_blank" rel="noopener">${esc(product.name)}</a>
        </h3>
        <p class="rf2-supporting-card__tagline">${esc(product.tagline || '')}</p>
        ${_concernChipHtml(product)}
        ${product.explanation ? `<button class="rf2-why-btn" type="button">Why recommended?</button>` : ''}
        <button class="btn btn--primary rf2-atb-btn"
                type="button"
                data-variant-id="${esc(product.shopifyVariantId || '')}"
                ${disableAttr}>
          ${esc(btnLabel)}
        </button>
      </div>
    `;

    article.querySelector('.rf2-atb-btn')?.addEventListener('click', (e) => {
      const vid = e.currentTarget.dataset.variantId;
      if (vid) _handleAddToCart(vid, e.currentTarget, product.id);
    });

    article.querySelector('.rf2-why-btn')?.addEventListener('click', (e) => {
      window.rfSupabase?.trackEvent(_sessionId, 'modal_opened', { product_id: product.id });
      _openModal(product, e.currentTarget);
    });

    return article;
  }

  // ─── Product detail modal ─────────────────────────────────────────────────
  /** Build the shared modal element once and append to body */
  function _createModal() {
    const el = document.createElement('div');
    el.className = 'rf2-modal-overlay';
    el.id        = 'rf2-modal-overlay';
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-labelledby', 'rf2-modal-name');
    el.hidden = true;

    el.innerHTML = `
      <div class="rf2-modal" role="document">
        <div class="rf2-modal__handle" aria-hidden="true"></div>
        <button class="rf2-modal__close" type="button" aria-label="Close">&#10005;</button>
        <div class="rf2-modal__media">
          <img id="rf2-modal-img" src="" alt="" loading="lazy">
          <div id="rf2-modal-img-ph" class="rf2-modal__img-placeholder" hidden></div>
        </div>
        <div class="rf2-modal__info">
          <p id="rf2-modal-step" class="rf2-step-label"></p>
          <h3 id="rf2-modal-name" class="rf2-modal__name"></h3>
          <p id="rf2-modal-tagline" class="rf2-modal__tagline"></p>
        </div>
        <div class="rf2-modal__detail">
          <div class="rf2-hero-card__why">
            <p class="rf2-why-label">Why this is recommended for you</p>
            <p id="rf2-modal-why" class="rf2-why-text"></p>
          </div>
          <p id="rf2-modal-sensitivity-tip" class="rf2-sensitivity-tip" hidden></p>
          <div id="rf2-modal-when-to-use"></div>
          <span id="rf2-modal-benefit" class="rf2-modal-concern-wrap"></span>
          <button id="rf2-modal-atb" class="btn btn--primary rf2-atb-btn" type="button"></button>
        </div>
      </div>
    `;

    document.body.appendChild(el);

    el.addEventListener('click', (e) => { if (e.target === el) _closeModal(); });
    document.addEventListener('keydown', (e) => {
      if (el.hidden) return;
      if (e.key === 'Escape') { _closeModal(); return; }
      // Focus trap — cycle Tab within modal
      if (e.key === 'Tab') {
        const focusable = el.querySelectorAll('button:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    el.querySelector('.rf2-modal__close').addEventListener('click', _closeModal);
    el.querySelector('#rf2-modal-atb').addEventListener('click', (e) => {
      const vid = e.currentTarget.dataset.variantId;
      const pid = e.currentTarget.dataset.productId;
      if (vid) _handleAddToCart(vid, e.currentTarget, pid);
    });

    // ── Touch drag-to-dismiss (mobile bottom sheet) + keyboard equivalent ──
    const handle    = el.querySelector('.rf2-modal__handle');
    const panel     = el.querySelector('.rf2-modal');
    let _dragStartY = 0;
    let _dragDeltaY = 0;

    // Make handle focusable + keyboard-dismissible
    handle.setAttribute('tabindex', '0');
    handle.setAttribute('role', 'button');
    handle.setAttribute('aria-label', 'Swipe down or press Enter to close');
    handle.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        _closeModal();
      }
    });

    handle.addEventListener('touchstart', (e) => {
      _dragStartY = e.touches[0].clientY;
      _dragDeltaY = 0;
      panel.style.transition = 'none';
    }, { passive: true });

    handle.addEventListener('touchmove', (e) => {
      _dragDeltaY = e.touches[0].clientY - _dragStartY;
      if (_dragDeltaY > 0) panel.style.transform = `translateY(${_dragDeltaY}px)`;
    }, { passive: true });

    handle.addEventListener('touchend', () => {
      panel.style.transition = '';
      if (_dragDeltaY > 80) {
        _closeModal();
      } else {
        panel.style.transform = '';
      }
      _dragStartY = 0;
      _dragDeltaY = 0;
    });

    return el;
  }

  function _openModal(product, triggerEl) {
    _lastFocusEl = triggerEl || document.activeElement;

    const price      = window.rfShopify?.formatPrice
      ? window.rfShopify.formatPrice(product.price, product.currencyCode)
      : `£${product.price}`;
    const outOfStock = product.available === false;
    const noVariant  = !product.shopifyVariantId;
    const btnLabel   = outOfStock ? 'Out of stock' : `Add to Bag \u2014 ${price}`;

    const img = _modal.querySelector('#rf2-modal-img');
    const ph  = _modal.querySelector('#rf2-modal-img-ph');
    if (product.imageUrl) {
      img.src = product.imageUrl; img.alt = product.name;
      img.hidden = false; ph.hidden = true;
    } else {
      img.hidden = true; ph.hidden = false;
    }

    _modal.querySelector('#rf2-modal-step').textContent    = product.stepName   || '';
    _modal.querySelector('#rf2-modal-name').textContent    = product.name       || '';
    _modal.querySelector('#rf2-modal-tagline').textContent = product.tagline    || '';
    _modal.querySelector('#rf2-modal-why').textContent     = product.explanation || '';

    // TPC sensitivity tip — separate element under the explanation
    const tipEl = _modal.querySelector('#rf2-modal-sensitivity-tip');
    if (_getFlags(product.id).sensitivityTip && _answers.sensitised === 'yes') {
      tipEl.textContent = 'If your skin is easily sensitised, start by applying a few drops of TPC into your moisturiser.';
      tipEl.hidden = false;
    } else {
      tipEl.hidden = true;
    }

    // When to use
    _modal.querySelector('#rf2-modal-when-to-use').innerHTML = _whenToUseHtml(product.id);

    const benefitEl = _modal.querySelector('#rf2-modal-benefit');
    if (product.concernKey) {
      benefitEl.innerHTML = _concernChipHtml(product);
      benefitEl.hidden = false;
    } else {
      benefitEl.hidden = true;
    }

    const atbBtn = _modal.querySelector('#rf2-modal-atb');
    atbBtn.textContent           = btnLabel;
    atbBtn.dataset.variantId     = product.shopifyVariantId || '';
    atbBtn.dataset.productId     = product.id || '';
    atbBtn.disabled              = outOfStock || noVariant;
    if (outOfStock || noVariant) atbBtn.setAttribute('aria-disabled', 'true');
    else                         atbBtn.removeAttribute('aria-disabled');

    // Lock body scroll (iOS-safe: position:fixed preserves content behind)
    const sy = window.scrollY;
    document.body.dataset.rfScrollY = sy;
    document.body.style.overflow   = 'hidden';
    document.body.style.position   = 'fixed';
    document.body.style.top        = `-${sy}px`;
    document.body.style.width      = '100%';

    _modal.hidden = false;
    requestAnimationFrame(() => _modal.classList.add('rf2-modal-overlay--open'));
    _modal.querySelector('.rf2-modal__close').focus();
  }

  function _closeModal() {
    // Clear any inline transform set during drag before CSS transition re-takes over
    const panel = _modal.querySelector('.rf2-modal');
    if (panel) { panel.style.transition = ''; panel.style.transform = ''; }

    _modal.classList.remove('rf2-modal-overlay--open');

    // Restore body scroll immediately (before animation ends)
    const sy = parseInt(document.body.dataset.rfScrollY || '0');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top      = '';
    document.body.style.width    = '';
    window.scrollTo(0, sy);
    delete document.body.dataset.rfScrollY;

    setTimeout(() => { _modal.hidden = true; }, 300);
    _lastFocusEl?.focus();
  }

  // ─── Video lightbox (full-screen playback) ────────────────────────────────
  function _createVideoLightbox() {
    const lb = document.createElement('div');
    lb.className = 'rf2-video-lightbox';
    lb.hidden    = true;
    lb.innerHTML = `
      <video class="rf2-video-lightbox__video" playsinline controls></video>
      <button class="rf2-video-lightbox__close" type="button" aria-label="Close video">&#10005;</button>
    `;
    lb.querySelector('.rf2-video-lightbox__close').addEventListener('click', _closeLightbox);
    lb.addEventListener('click', (e) => { if (e.target === lb) _closeLightbox(); });
    document.body.appendChild(lb);
    return lb;
  }

  function _openLightbox(src, label) {
    const vid = _lightbox.querySelector('video');
    vid.src = src;
    vid.setAttribute('aria-label', label || 'Product video');
    _lightbox.hidden = false;
    requestAnimationFrame(() => _lightbox.classList.add('rf2-video-lightbox--open'));
    vid.play().catch(() => {}); // autoplay may be blocked — controls shown so user can tap
  }

  function _closeLightbox() {
    const vid = _lightbox.querySelector('video');
    vid.pause();
    vid.src = '';
    _lightbox.classList.remove('rf2-video-lightbox--open');
    setTimeout(() => { _lightbox.hidden = true; }, 240);
  }

  // ─── Free-shipping thresholds (mirrors Shopify settings) ────────────────
  // Values for fixed-currency markets are in their presentment currency.
  // All other countries use the DEFAULT (USD) and convert at runtime:
  //   local = (usd_threshold / GBP_USD_RATE) × window.Shopify.currency.rate
  const _FS_THRESHOLDS = {
    US: 75, GB: 50, EU: 115, AU: 150, CA: 150, NO: 300,
    SG: 300, CH: 300, NZ: 350, AE: 350, MY: 350, SA: 350,
    JP: 350, KR: 350, HK: 350, IN: 350, DEFAULT: 190,
  };
  // Fixed-currency markets — threshold is already in local currency
  const _FS_FIXED = ['GBP', 'EUR', 'USD', 'CAD', 'NOK'];
  const _GBP_USD_RATE = 1.33;

  // EU country codes (for EU=115 lookup)
  const _EU_CODES = new Set(
    'AT,BE,BG,HR,CY,CZ,DK,EE,FI,FR,DE,GR,HU,IE,IT,LV,LT,LU,MT,NL,PL,PT,RO,SK,SI,ES,SE'.split(',')
  );

  /** Returns the free-shipping threshold in the visitor's local currency (numeric). */
  function _getFreeShippingAmount() {
    const country  = (window.Shopify?.country || '').toUpperCase();
    const currency = (window.Shopify?.currency?.active || 'GBP').toUpperCase();
    const raw      = _FS_THRESHOLDS[country]
                  || (_EU_CODES.has(country) ? (_FS_THRESHOLDS.EU || _FS_THRESHOLDS.DEFAULT) : 0)
                  || _FS_THRESHOLDS.DEFAULT;
    if (_FS_FIXED.includes(currency)) return Math.ceil(raw);
    const gbpToLocal = Number(window.Shopify?.currency?.rate) || 1;
    return Math.ceil((raw / _GBP_USD_RATE) * gbpToLocal);
  }

  function _getFreeShippingText() {
    const currency = (window.Shopify?.currency?.active || 'GBP').toUpperCase();
    const amount   = _getFreeShippingAmount();
    try {
      const formatted = new Intl.NumberFormat(undefined, {
        style: 'currency', currency, currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 0, maximumFractionDigits: 0,
      }).format(amount);
      return `Free shipping over ${formatted}`;
    } catch (_) {
      return `Free shipping over ${currency} ${amount}`;
    }
  }

  // ─── Bundle constants ───────────────────────────────────────────────────
  const BUNDLE_FUNCTION_URL = 'https://yupexdimsfuswwkouewp.supabase.co/functions/v1/sapp-bundle-variant';
  function _getBundleDiscount(count) { return count >= 4 ? 20 : count >= 3 ? 15 : 0; }

  // ─── Cart ─────────────────────────────────────────────────────────────────
  async function _handleAddToCart(variantId, btn, productId) {
    if (!window.rfShopify?.addToCart) return;
    const original  = btn.textContent.trim();
    btn.disabled    = true;
    btn.textContent = 'Adding…';
    try {
      await window.rfShopify.addToCart(variantId, 1);

      // Set cart-level Routine Finder attribution (order-level in Shopify admin)
      if (_recs && window.rfShopify?.setCartAttributes) {
        window.rfShopify.setCartAttributes(_answers, _recs.hero.id, _sessionId);
      }

      // Track ATC event
      window.rfSupabase?.trackEvent(_sessionId, 'atc_single', {
        product_id: productId || null,
        variant_id: variantId,
      });

      btn.textContent = '✓ Added';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2200);
    } catch (_) {
      btn.textContent = 'Error – try again';
      btn.disabled    = false;
    }
  }

  async function handleAddFullRoutine() {
    if (!_recs) return;
    const btn      = $('rf2-add-routine');
    const products = [_recs.hero, ..._recs.supporting]
      .filter(p => p.available !== false && p.shopifyVariantId);

    if (!products.length) return;

    btn.disabled = true;
    btn.setAttribute('aria-busy', 'true');
    btn.textContent = 'Adding routine…';

    const discount    = _getBundleDiscount(products.length);
    const countryCode = (window.Shopify && window.Shopify.country) || 'XX';

    try {
      if (discount > 0) {
        // ── Bundle path: call Supabase function for a discounted bundle variant ──
        const variantIds = products.map(p => parseInt(p.shopifyVariantId, 10));
        const productIds = products.map(p => p.shopifyProductId).filter(Boolean);
        const titles     = products.map(p => p.name || '');

        const bundleRes = await fetch(BUNDLE_FUNCTION_URL, {
          method:  'POST',
          headers: { 'Content-Type': 'application/json' },
          body:    JSON.stringify({
            variant_ids:    variantIds,
            product_ids:    productIds,
            variant_titles: titles,
            discount_value: discount,
            country_code:   countryCode,
          }),
        });
        if (!bundleRes.ok) {
          const err = await bundleRes.json().catch(() => ({}));
          throw new Error(err.error || bundleRes.statusText);
        }
        const bundleData = await bundleRes.json();
        if (!bundleData.bundle_variant_id) throw new Error('No bundle_variant_id');

        // Add single bundle variant to cart via FoxTheme-aware helper
        await window.rfShopify.addToCartItems([{ id: bundleData.bundle_variant_id, quantity: 1 }]);
      } else {
        // ── Fewer than 3 items — add individually ──
        const items = products.map(p => ({ id: p.shopifyVariantId, quantity: 1 }));
        await window.rfShopify.addToCartItems(items);
      }

      // Set cart-level Routine Finder attribution
      if (window.rfShopify?.setCartAttributes) {
        window.rfShopify.setCartAttributes(_answers, _recs.hero.id, _sessionId);
      }

      // Track full routine ATC event
      window.rfSupabase?.trackEvent(_sessionId, 'atc_routine', {
        metadata: {
          products:  products.map(p => ({ id: p.id, variant_id: p.shopifyVariantId })),
          count:     products.length,
          discount:  discount,
          bundled:   discount > 0,
        },
      });

      btn.textContent = '✓ Full Routine Added';
      btn.removeAttribute('aria-busy');
    } catch (e) {
      console.error('[RF] bundle add-to-cart failed', e);
      btn.textContent = 'Error – try again';
      btn.disabled    = false;
      btn.removeAttribute('aria-busy');
    }
  }

  // ─── Klaviyo ──────────────────────────────────────────────────────────────
  function _trackKlaviyo(recs) {
    if (!_email || typeof window._learnq === 'undefined') return;
    window._learnq.push(['identify', { '$email': _email }]);
    window._learnq.push(['track', 'Skincare Quiz Completed', {
      email:                _email,
      age_bracket:          _answers.age_bracket,
      gender:               _answers.gender,
      primary_concern:      _answers.primary_concern,
      secondary_concern:    _answers.secondary_concern,
      sensitised:           _answers.sensitised,
      pregnant:             _answers.pregnant,
      menopause:            _answers.menopause,
      hero_product:         recs.hero.id,
      recommended_products: [recs.hero, ...recs.supporting].map(p => p.id),
    }]);
  }

  // ─── Cross-device load ────────────────────────────────────────────────────
  async function loadRoutineByToken(token) {
    if (!window.rfSupabase?.getQuizByToken) { showScreen('intro'); return; }
    showScreen('loading');
    const data = await window.rfSupabase.getQuizByToken(token);
    if (data?.answers) {
      _answers = data.answers;
      _email   = data.email || '';
      await processResults();
    } else {
      showScreen('intro');
    }
  }

  // ─── Returning user restore ───────────────────────────────────────────────
  /**
   * Restores a saved session for a returning user.
   * Primary path: re-fetches fresh Shopify-enriched results via share_token.
   * Fallback: re-runs the recommendation engine locally from saved answers
   *           (handles offline / expired token gracefully).
   */
  async function _restoreSavedSession(saved) {
    // Restore session_id so any further events (ATC, modal) are attributed
    if (saved.session_id) _sessionId = saved.session_id;

    // Primary: load via token for fresh Shopify data (prices, availability, images)
    if (saved.share_token && window.rfSupabase?.getQuizByToken) {
      showScreen('loading');
      const data = await window.rfSupabase.getQuizByToken(saved.share_token);
      if (data?.answers) {
        _answers = data.answers;
        _email   = data.email || '';
        await processResults();
        _showWelcomeBackBanner(true);
        return;
      }
    }
    // Fallback: re-run recommendation engine from locally saved answers
    // (works offline; Shopify enrichment is still attempted and degrades gracefully)
    if (saved.answers && Object.keys(saved.answers).length) {
      _answers = saved.answers;
      await processResults();
      _showWelcomeBackBanner(true);
      return;
    }
    // Unresolvable — clear stale entry and show intro
    _clearSavedSession();
    showScreen('intro');
  }

  /** Show or hide the welcome-back banner inside the results screen. */
  function _showWelcomeBackBanner(show) {
    const banner = $('rf2-welcome-banner');
    if (banner) banner.hidden = !show;
  }

  // ─── Utilities ────────────────────────────────────────────────────────────
  function startOver() {
    _step      = 0;
    _answers   = {};
    _email     = '';
    _recs      = null;
    _sessionId = null;
    $('rf2-supporting-divider').hidden = true;
    $('rf2-routine-cta').hidden        = true;
    $('rf2-supporting-list').innerHTML = '';
    $('rf2-hero-section').innerHTML    = '';
    _clearSavedSession();
    _showWelcomeBackBanner(false);
    startQuiz();
  }

  function _getSavedEmail() { try { return localStorage.getItem('rf_email') || ''; } catch (_) { return ''; } }
  function _setSavedEmail(v) { try { localStorage.setItem('rf_email', v); } catch (_) {} }

  // ─── Returning-user session persistence ───────────────────────────────────
  const RF_SESSION_KEY = 'rf_session';
  const RF_SESSION_TTL = 30 * 24 * 60 * 60 * 1000; // 30 days in ms

  function _loadSavedSession() {
    try {
      const raw = localStorage.getItem(RF_SESSION_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Accept sessions with either a share_token (full quiz) or answers (mini quiz)
      if ((!parsed?.share_token && !parsed?.answers) || !parsed?.savedAt) return null;
      if (Date.now() - parsed.savedAt > RF_SESSION_TTL) {
        localStorage.removeItem(RF_SESSION_KEY);
        return null;
      }
      return parsed;
    } catch (_) { return null; }
  }

  function _saveSession(shareToken, answers, heroId, sessionId) {
    try {
      localStorage.setItem(RF_SESSION_KEY, JSON.stringify({
        share_token: shareToken,
        answers:     answers,
        hero_id:     heroId || null,
        session_id:  sessionId || null,
        savedAt:     Date.now(),
      }));
    } catch (_) { /* non-critical — private browsing or storage quota */ }
  }

  function _clearSavedSession() {
    try { localStorage.removeItem(RF_SESSION_KEY); } catch (_) {}
  }

  // ─── PDP Mini Quiz ─────────────────────────────────────────────────────
  // Compact 6-question modal for product pages.  Reuses the same scoring
  // engine (rfResults) and Shopify helpers (rfShopify).  Activated when the
  // PDP badge snippet sets window.rfMiniQuiz = { active: true, … }.
  // All DOM uses the rfm- prefix to avoid ID collisions with the full quiz.

  const MINI_QUESTION_IDS = ['age_bracket', 'gender', 'primary_concern', 'secondary_concern', 'sensitised', 'pregnant'];
  const MINI_DEFAULTS     = { menopause: 'no' };

  let _miniStep       = 0;
  let _miniAnswers    = {};
  let _miniQuestions   = [];
  let _miniOverlay    = null;
  let _miniSessionId  = null; // Supabase tracking session for PDP mini quiz
  let _miniStartedAt  = null; // Timestamp for time-spent tracking
  let _miniCompleted  = false; // True after quiz finishes successfully

  async function _initMini() {
    const cfg = window.rfMiniQuiz;
    _miniStep    = 0;
    _miniAnswers = { ...MINI_DEFAULTS };
    _miniStartedAt = Date.now();
    _miniQuestions = window.rfQuestions.filter(q => MINI_QUESTION_IDS.includes(q.id));

    // Restore trigger button from "Loading quiz…" state
    const triggerBtn = document.getElementById('rfm-trigger');
    if (triggerBtn) {
      triggerBtn.disabled = false;
      triggerBtn.innerHTML = 'Is this right for my skin? <span style="text-decoration:underline">Take the quiz</span>';
    }

    if (!_miniOverlay) {
      _miniOverlay = _buildMiniModal();
      document.body.appendChild(_miniOverlay);
    }
    _renderMiniQuestion();
    _showMiniScreen('quiz');
    _miniOverlay.hidden = false;
    requestAnimationFrame(() => _miniOverlay.classList.add('rfm-overlay--open'));
    // Lock scroll
    const sy = window.scrollY;
    document.body.dataset.rfmScrollY = sy;
    document.body.style.overflow  = 'hidden';
    document.body.style.position  = 'fixed';
    document.body.style.top       = `-${sy}px`;
    document.body.style.width     = '100%';
    // Focus close button for accessibility
    _miniOverlay.querySelector('.rfm-close')?.focus();

    // Start a Supabase tracking session for PDP mini quiz
    if (window.rfSupabase?.startSession) {
      const res = await window.rfSupabase.startSession();
      if (res?.session_id) {
        _miniSessionId = res.session_id;
        window.rfSupabase.updateSession(_miniSessionId, {
          source:      'pdp_mini',
          pdp_product: cfg.productId || null,
          pdp_handle:  cfg.productHandle || null,
        });
      }
    }

    // Track trigger click + mini quiz open
    window.rfSupabase?.trackEvent(_miniSessionId, 'pdp_trigger_click', {
      metadata: { pdp_product: cfg.productId, pdp_handle: cfg.productHandle },
    });
    window.rfSupabase?.trackEvent(_miniSessionId, 'mini_quiz_open', {
      metadata: { pdp_product: cfg.productId, pdp_handle: cfg.productHandle },
    });
  }

  function _buildMiniModal() {
    const el = document.createElement('div');
    el.className = 'rfm-overlay';
    el.hidden    = true;
    el.setAttribute('role', 'dialog');
    el.setAttribute('aria-modal', 'true');
    el.setAttribute('aria-label', 'Is this right for my skin?');
    el.innerHTML = `
      <div class="rfm-modal">
        <button class="rfm-close" type="button" aria-label="Close">&times;</button>

        <div class="rfm-screen rfm-screen--quiz" data-rfm-screen="quiz">
          <p class="rfm-counter" id="rfm-counter"></p>
          <h2 class="rfm-q-title" id="rfm-q-title"></h2>
          <p  class="rfm-q-subtitle" id="rfm-q-subtitle"></p>
          <div class="rfm-options" id="rfm-options"></div>
          <button class="rfm-back" id="rfm-back" type="button" hidden>&larr; Back</button>
        </div>

        <div class="rfm-screen rfm-screen--loading" data-rfm-screen="loading" hidden>
          <div class="rfm-spinner" aria-label="Analysing your answers"></div>
          <p class="rfm-loading-text">Finding your match…</p>
        </div>

        <div class="rfm-screen rfm-screen--result" data-rfm-screen="result" hidden>
          <div id="rfm-result-content"></div>
        </div>
      </div>
    `;

    // Wire close button + backdrop click + Escape + focus trap
    el.querySelector('.rfm-close').addEventListener('click', _closeMini);
    el.addEventListener('click', (e) => { if (e.target === el) _closeMini(); });
    document.addEventListener('keydown', (e) => {
      if (!_miniOverlay || _miniOverlay.hidden) return;
      if (e.key === 'Escape') { _closeMini(); return; }
      // Focus trap — cycle Tab within mini quiz modal
      if (e.key === 'Tab') {
        const focusable = _miniOverlay.querySelectorAll('button:not([disabled]):not([hidden]), a[href], [tabindex]:not([tabindex="-1"])');
        if (!focusable.length) return;
        const first = focusable[0];
        const last  = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
    // Wire back button
    el.querySelector('#rfm-back').addEventListener('click', _miniPrevStep);

    return el;
  }

  function _showMiniScreen(name) {
    _miniOverlay.querySelectorAll('.rfm-screen').forEach(s => {
      s.hidden = s.dataset.rfmScreen !== name;
    });
  }

  function _renderMiniQuestion() {
    const q     = _miniQuestions[_miniStep];
    const total = _miniQuestions.length;

    _miniOverlay.querySelector('#rfm-counter').textContent = `${_miniStep + 1} / ${total}`;
    _miniOverlay.querySelector('#rfm-q-title').textContent = q.title;

    const sub = _miniOverlay.querySelector('#rfm-q-subtitle');
    sub.textContent = q.subtitle || '';
    sub.hidden      = !q.subtitle;

    _miniOverlay.querySelector('#rfm-back').hidden = _miniStep === 0;

    // Filter options (secondary_concern removes primary choice)
    let opts = q.options;
    if (q.filterOut && _miniAnswers[q.filterOut]) {
      opts = opts.filter(o => o.value !== _miniAnswers[q.filterOut]);
    }

    const container = _miniOverlay.querySelector('#rfm-options');
    // Use 2-col grid only for short-label questions (yes/no — 4 options or fewer)
    container.className = `rfm-options${opts.length <= 4 ? ' rfm-options--grid' : ''}`;
    container.innerHTML = '';

    opts.forEach((opt, idx) => {
      const btn = document.createElement('button');
      btn.type          = 'button';
      btn.className     = `rfm-option${_miniAnswers[q.id] === opt.value ? ' rfm-option--selected' : ''}`;
      btn.textContent   = opt.label;
      btn.dataset.value = opt.value;
      btn.setAttribute('role', 'option');
      btn.setAttribute('aria-selected', _miniAnswers[q.id] === opt.value ? 'true' : 'false');
      btn.addEventListener('click', () => _miniSelectOption(q.id, opt.value, btn));
      // Arrow key navigation between options
      btn.addEventListener('keydown', (e) => {
        const siblings = Array.from(container.querySelectorAll('.rfm-option'));
        let next;
        if (e.key === 'ArrowDown' || e.key === 'ArrowRight') {
          e.preventDefault();
          next = siblings[idx + 1] || siblings[0];
        } else if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') {
          e.preventDefault();
          next = siblings[idx - 1] || siblings[siblings.length - 1];
        }
        if (next) next.focus();
      });
      container.appendChild(btn);
    });
  }

  let _miniOptionLocked = false; // debounce guard for mini quiz options

  function _miniSelectOption(questionId, value, clickedBtn) {
    if (_miniOptionLocked) return;
    _miniOptionLocked = true;
    _miniAnswers[questionId] = value;
    _miniOverlay.querySelectorAll('#rfm-options .rfm-option').forEach(btn => {
      btn.classList.toggle('rfm-option--selected', btn === clickedBtn);
    });
    // Track step completion
    window.rfSupabase?.trackEvent(_miniSessionId, 'mini_quiz_step', {
      metadata: { question: questionId, value: value, step: _miniStep + 1 },
    });
    window.rfSupabase?.updateSession(_miniSessionId, { last_step_reached: _miniStep + 1 });
    setTimeout(() => { _miniOptionLocked = false; _miniAdvance(); }, 280);
  }

  function _miniAdvance() {
    if (_miniStep < _miniQuestions.length - 1) {
      _miniStep++;
      _renderMiniQuestion();
    } else {
      _processMiniResults();
    }
  }

  function _miniPrevStep() {
    if (_miniStep > 0) { _miniStep--; _renderMiniQuestion(); }
  }

  async function _processMiniResults() {
    _showMiniScreen('loading');

    try {
      // Run the same recommendation engine with defaults for skipped questions
      const recs = window.rfResults.generateRoutineRecommendations(_miniAnswers);
      const all  = [recs.hero, ...recs.supporting];

      // Enrich hero (and the current product if different) with live Shopify data
      if (window.rfShopify?.enrichProducts) {
        await window.rfShopify.enrichProducts(all);
      }

      // Personalized explanations
      if (window.explanationService?.generatePersonalizedExplanation) {
        all.forEach(p => {
          p.explanation = window.explanationService.generatePersonalizedExplanation(p.id, _miniAnswers);
        });
      }

      // Determine match: does the hero (or any product) match the current PDP product?
      const cfg       = window.rfMiniQuiz;
      const currentId = cfg.productId;
      const isMatch   = recs.hero.id === currentId;

      // Save to localStorage only if no existing full quiz session
      try {
        const existing = localStorage.getItem('rf_session');
        if (!existing) {
          localStorage.setItem('rf_session', JSON.stringify({
            source:     'pdp_mini',
            answers:    _miniAnswers,
            hero_id:    recs.hero.id,
            session_id: _miniSessionId || null,
            savedAt:    Date.now(),
          }));
        }
      } catch (_) {}

      // Supabase analytics — complete the mini quiz session
      const miniTimeSpent = _miniStartedAt ? Math.round((Date.now() - _miniStartedAt) / 1000) : null;
      window.rfSupabase?.completeSession?.(_miniSessionId, {
        completed:            true,
        answers:              _miniAnswers,
        hero_product:         recs.hero.id,
        recommended_products: all.map(p => p.id),
        source:               'pdp_mini',
        time_spent_seconds:   miniTimeSpent,
      });
      window.rfSupabase?.trackEvent(_miniSessionId, 'mini_quiz_complete', {
        metadata: {
          answers:     _miniAnswers,
          hero:        recs.hero.id,
          pdp_product: currentId,
          is_match:    isMatch,
        },
      });

      _renderMiniResult(recs.hero, isMatch, all);
      _showMiniScreen('result');
    } catch (err) {
      console.error('[RF] _processMiniResults failed:', err);
      // Show error in the mini quiz loading screen
      var loadingText = _miniOverlay?.querySelector('.rfm-loading-text');
      if (loadingText) {
        loadingText.textContent = 'Something went wrong \u2014 please try again.';
      }
      setTimeout(_closeMini, 3000);
    }
  }

  function _renderMiniResult(hero, isMatch, allProducts) {
    const cfg     = window.rfMiniQuiz;
    const LABELS  = window.rfResults.CONCERN_LABELS;
    const content = _miniOverlay.querySelector('#rfm-result-content');
    const price   = window.rfShopify?.formatPrice
      ? window.rfShopify.formatPrice(hero.price, hero.currencyCode)
      : `\u00A3${hero.price}`;

    // Build concern chips — show both primary and secondary when applicable
    const primaryChip   = _concernChipHtml(hero);
    const secondaryChip = hero.secondaryConcernKey
      ? _concernChipHtml({ ...hero, concernType: 'secondary', concernKey: hero.secondaryConcernKey })
      : '';
    const chipsHtml = (primaryChip || secondaryChip)
      ? `<div class="rfm-result__concerns">${primaryChip}${secondaryChip}</div>`
      : '';

    // Build sensitivity tip for TPC if applicable
    const tpcTipHtml = (_getFlags(hero.id).sensitivityTip && _miniAnswers.sensitised === 'yes')
      ? '<p class="rf2-sensitivity-tip">If your skin is easily sensitised, start by applying a few drops of TPC into your moisturiser.</p>'
      : '';

    if (isMatch) {
      // ── MATCH: this product IS the hero recommendation ──
      content.innerHTML = `
        <div class="rfm-result rfm-result--match">
          <span class="rfm-result__icon" aria-hidden="true">&#10003;</span>
          <h3 class="rfm-result__heading">Yes — this is formulated for your skin</h3>
          <p class="rfm-result__detail">
            Based on your answers, <strong>${esc(hero.name)}</strong> is your
            #1 recommendation for <strong>${esc(LABELS[_miniAnswers.primary_concern] || '')}</strong>.
          </p>
          ${chipsHtml}
          ${hero.explanation ? `<p class="rfm-result__why">${esc(hero.explanation)}</p>` : ''}
          ${tpcTipHtml}
          ${_whenToUseHtml(hero.id)}
          <button class="btn btn--primary rfm-atc-btn" type="button"
                  data-variant-id="${esc(hero.shopifyVariantId || '')}">
            Add to Bag &mdash; ${esc(price)}
          </button>
          <button class="rfm-full-routine-link" type="button">
            Learn More
          </button>
        </div>
      `;
      content.querySelector('.rfm-atc-btn')?.addEventListener('click', (e) => {
        const vid = e.currentTarget.dataset.variantId;
        if (vid) _handleMiniAtc(vid, e.currentTarget, hero.id);
      });
      content.querySelector('.rfm-full-routine-link')?.addEventListener('click', _goToFullRoutine);
    } else {
      // ── NO MATCH: a different product is the hero ──
      content.innerHTML = `
        <div class="rfm-result rfm-result--no-match">
          <h3 class="rfm-result__heading">We have a stronger match for you</h3>
          <p class="rfm-result__detail">
            Based on your answers, your #1 recommendation is:
          </p>
          <a class="rfm-hero-card" href="${esc(hero.productUrl || '#')}">
            ${hero.imageUrl
              ? `<img class="rfm-hero-card__img" src="${esc(hero.imageUrl)}" alt="${esc(hero.name)}" loading="lazy" width="200" height="250">`
              : ''
            }
            <span class="rfm-hero-card__name">${esc(hero.name)}</span>
            <span class="rfm-hero-card__tagline">${esc(hero.tagline || '')}</span>
          </a>
          ${chipsHtml}
          <button class="rfm-full-routine-link" type="button">
            Learn More
          </button>
        </div>
      `;
      content.querySelector('.rfm-full-routine-link')?.addEventListener('click', _goToFullRoutine);
    }
  }

  async function _handleMiniAtc(variantId, btn, productId) {
    if (!window.rfShopify?.addToCart) return;
    const original  = btn.textContent.trim();
    btn.disabled    = true;
    btn.textContent = 'Adding\u2026';
    try {
      await window.rfShopify.addToCart(variantId, 1);
      window.rfSupabase?.trackEvent(_miniSessionId, 'mini_atc', {
        product_id: productId, variant_id: variantId,
        metadata: { source: 'pdp_mini' },
      });
      btn.textContent = '\u2713 Added';
      setTimeout(() => { btn.textContent = original; btn.disabled = false; }, 2200);
    } catch (_) {
      btn.textContent = 'Error \u2013 try again';
      btn.disabled    = false;
    }
  }

  function _closeMini() {
    if (!_miniOverlay) return;
    _miniOverlay.classList.remove('rfm-overlay--open');

    if (_miniCompleted) {
      // Quiz finished — hide trigger and show badge
      const triggerWrap = document.getElementById('rfm-trigger-wrap');
      if (triggerWrap) triggerWrap.hidden = true;
      const badge  = document.getElementById('rf-pdp-badge');
      if (badge) {
        const target = document.getElementById('buy_buttons')
                    || document.querySelector('product-form.f-main-product-form')
                    || document.querySelector('.f-product-form__buttons');
        if (target && target.parentNode) target.parentNode.insertBefore(badge, target.nextSibling);
        badge.hidden = false;
      }
    } else {
      // Restore trigger button in case it's still in loading state
      const triggerBtn = document.getElementById('rfm-trigger');
      if (triggerBtn) {
        triggerBtn.disabled = false;
        triggerBtn.innerHTML = 'Is this right for my skin? <span style="text-decoration:underline">Take the quiz</span>';
      }
    }
    // Restore scroll
    const sy = parseInt(document.body.dataset.rfmScrollY || '0');
    document.body.style.overflow  = '';
    document.body.style.position  = '';
    document.body.style.top       = '';
    document.body.style.width     = '';
    window.scrollTo(0, sy);
    delete document.body.dataset.rfmScrollY;
    setTimeout(() => { _miniOverlay.hidden = true; }, 300);
  }

  /** Navigate to the full routine finder page with mini quiz answers pre-loaded. */
  function _goToFullRoutine() {
    window.rfSupabase?.trackEvent(_miniSessionId, 'mini_full_routine_click', {
      metadata: { pdp_product: window.rfMiniQuiz?.productId },
    });
    // Save answers to localStorage so the full quiz page can restore from them
    try {
      localStorage.setItem(RF_SESSION_KEY, JSON.stringify({
        source:   'pdp_mini',
        answers:  _miniAnswers,
        hero_id:  null,
        savedAt:  Date.now(),
      }));
    } catch (_) {}
    _closeMini();
    window.location.href = '/pages/routine-finder';
  }

  /** Public API: re-open the mini quiz without reloading scripts. */
  window.rfMiniOpen = function (cfg) {
    if (cfg) Object.assign(window.rfMiniQuiz, cfg);
    window.rfMiniQuiz.active = true;
    _initMini();
  };

  // ─── Bootstrap ────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
